import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc, writeBatch } from 'firebase/firestore';
import { ArrowLeft, CreditCard as Edit2, Trash2, Plus, Save, X, Download, FileDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BusCompany } from '../types';

const INITIAL_DATA = [
  { company: 'Alltours', country: 'EST' },
  { company: 'ARK tourist', country: 'PL' },
  { company: 'Axelssons', country: 'S' },
  { company: 'Baltic Shuttle', country: 'EST' },
  { company: 'Blitz Reisen', country: 'D' },
  { company: 'Bondrida', country: 'LT' },
  { company: 'Bussbookning.com', country: 'S' },
  { company: 'Euro Trans', country: 'PL' },
  { company: 'Europabus', country: 'PL' },
  { company: 'Europamundo', country: 'E' },
  { company: 'Fibus', country: 'FIN' },
  { company: 'Follow Me', country: 'PL' },
  { company: 'G2Travel', country: 'PL' },
  { company: 'Globus', country: 'PL' },
  { company: 'Go Bus', country: 'EST' },
  { company: 'Go 365 Travel', country: 'EST' },
  { company: 'Hansabuss', country: 'EST' },
  { company: 'Indre reisid', country: 'EST' },
  { company: 'Insight vacations', country: 'H' },
  { company: 'Interbus', country: 'S' },
  { company: 'Kimibus', country: 'PL' },
  { company: 'Kautra', country: 'LT' },
  { company: 'Let\'s Go', country: 'S' },
  { company: 'Lux Charter', country: 'EST' },
  { company: 'Ollex', country: 'LT' },
  { company: 'Primax Trans', country: 'PL' },
  { company: 'Radek Tour', country: 'PL' },
  { company: 'Rainbow', country: 'PL' },
  { company: 'Rakso', country: 'PL' },
  { company: 'Ros trans', country: 'PL' },
  { company: 'Salaün holidays', country: 'F' },
  { company: 'Scandic Way', country: 'FIN' },
  { company: 'Sindbad', country: 'PL' },
  { company: 'Svirka', country: 'LT' },
  { company: 'Taisto', country: 'EST' },
  { company: 'Trafalgar', country: 'PL' },
  { company: 'Twerenbold', country: 'L' },
  { company: 'Unitours', country: 'FIN' },
  { company: 'Vilneda', country: 'LT' },
  { company: 'VIP coach service', country: 'PL' },
  { company: 'Westin Buss', country: 'S' },
  { company: 'Wiktoria', country: 'PL' },
  { company: 'Wirazbus', country: 'PL' },
  { company: 'Zawisza', country: 'PL' }
];

export default function ManageBusCompanies() {
  const { signOut } = useAuth();
  const [busCompanies, setBusCompanies] = useState<(BusCompany & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<Partial<BusCompany>>({});
  const [newCompany, setNewCompany] = useState<Partial<BusCompany>>({});
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchBusCompanies();
  }, []);

  const fetchBusCompanies = async () => {
    try {
      const busCompaniesSnapshot = await getDocs(collection(db, 'bus-companies'));
      const busCompaniesData = busCompaniesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as (BusCompany & { id: string })[];
      
      // Sort companies alphabetically by company name
      busCompaniesData.sort((a, b) => a.company.localeCompare(b.company));
      setBusCompanies(busCompaniesData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bus companies:', error);
      setLoading(false);
    }
  };


  const handleEdit = (company: BusCompany & { id: string }) => {
    setEditingId(company.id);
    setEditingData({
      company: company.company,
      country: company.country
    });
  };

  const handleSave = async (id: string) => {
    if (!editingData.company?.trim() || !editingData.country?.trim()) {
      alert('Både selskap og land er påkrevd');
      return;
    }
    
    try {
      await updateDoc(doc(db, 'bus-companies', id), {
        company: editingData.company.trim(),
        country: editingData.country.trim()
      });
      
      setBusCompanies(busCompanies.map(company => 
        company.id === id ? { 
          ...company, 
          company: editingData.company!.trim(),
          country: editingData.country!.trim()
        } : company
      ));
      
      setEditingId(null);
      setEditingData({});
    } catch (error) {
      console.error('Error updating bus company:', error);
      alert('Feil ved oppdatering av busselskap');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Er du sikker på at du vil slette dette busselskapet?')) return;
    
    try {
      await deleteDoc(doc(db, 'bus-companies', id));
      setBusCompanies(busCompanies.filter(company => company.id !== id));
    } catch (error) {
      console.error('Error deleting bus company:', error);
      alert('Feil ved sletting av busselskap');
    }
  };

  const handleAddCompany = async () => {
    if (!newCompany.company?.trim() || !newCompany.country?.trim()) {
      alert('Både selskap og land er påkrevd');
      return;
    }
    
    try {
      const docRef = await addDoc(collection(db, 'bus-companies'), {
        company: newCompany.company.trim(),
        country: newCompany.country.trim()
      });
      
      const newBusCompany = {
        id: docRef.id,
        company: newCompany.company.trim(),
        country: newCompany.country.trim()
      };
      
      setBusCompanies([...busCompanies, newBusCompany].sort((a, b) => a.company.localeCompare(b.company)));
      setNewCompany({});
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding bus company:', error);
      alert('Feil ved tillegging av busselskap');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingData({});
  };

  const exportCompanies = () => {
    const csvContent = [
      'Company,Country',
      ...busCompanies.map(company => `"${company.company}","${company.country}"`)
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `busselskaper_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportJSON = () => {
    const exportData = busCompanies.map(company => ({
      company: company.company,
      country: company.country
    }));

    const jsonContent = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `busselskaper_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <Link
                to="/"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors duration-150"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Tilbake til dashboard
              </Link>
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Administrer busselskaper</h1>
            <button
              onClick={() => signOut()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Logg ut
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">

          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900">
                Busselskaper ({busCompanies.length})
              </h2>
              <div className="flex items-center space-x-3">
                <button
                  onClick={exportCompanies}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Eksporter CSV
                </button>
                <button
                  onClick={exportJSON}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <FileDown className="h-4 w-4 mr-2" />
                  Eksporter JSON
                </button>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Legg til nytt selskap
                </button>
              </div>
            </div>

            {showAddForm && (
              <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Selskapsnavn
                    </label>
                    <input
                      type="text"
                      value={newCompany.company || ''}
                      onChange={(e) => setNewCompany({...newCompany, company: e.target.value})}
                      placeholder="Navn på busselskap"
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Land
                    </label>
                    <input
                      type="text"
                      value={newCompany.country || ''}
                      onChange={(e) => setNewCompany({...newCompany, country: e.target.value})}
                      placeholder="Landkode (f.eks. PL, EST, S)"
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleAddCompany}
                      disabled={!newCompany.company?.trim() || !newCompany.country?.trim()}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
                    >
                      <Save className="h-4 w-4 mr-1" />
                      Lagre
                    </button>
                    <button
                      onClick={() => {
                        setShowAddForm(false);
                        setNewCompany({});
                      }}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Avbryt
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Selskapsnavn
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Land
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Handlinger
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {busCompanies.map((company, index) => (
                    <tr key={company.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingId === company.id ? (
                          <input
                            type="text"
                            value={editingData.company || ''}
                            onChange={(e) => setEditingData({...editingData, company: e.target.value})}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            autoFocus
                          />
                        ) : (
                          <div className="text-sm font-medium text-gray-900">{company.company}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingId === company.id ? (
                          <input
                            type="text"
                            value={editingData.country || ''}
                            onChange={(e) => setEditingData({...editingData, country: e.target.value})}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        ) : (
                          <div className="text-sm text-gray-900">{company.country}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {editingId === company.id ? (
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleSave(company.id)}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                            >
                              <Save className="h-3 w-3 mr-1" />
                              Lagre
                            </button>
                            <button
                              onClick={handleCancel}
                              className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                            >
                              <X className="h-3 w-3 mr-1" />
                              Avbryt
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleEdit(company)}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-blue-600 hover:text-blue-900"
                            >
                              <Edit2 className="h-3 w-3 mr-1" />
                              Rediger
                            </button>
                            <button
                              onClick={() => handleDelete(company.id)}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Slett
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {busCompanies.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-gray-500">
                    <p className="text-lg font-medium">Ingen busselskaper registrert</p>
                    <p className="mt-2">Klikk på "Legg til nytt selskap" for å komme i gang.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}